import React, { memo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Clock, RefreshCw } from 'lucide-react';
import { Button } from '../../../../../components/ui/Button';
import { ConfirmDialog } from '../../../../../components/ui/ConfirmDialog';
import { retryMeliAuth } from '../../../../../features/ecommerces/ecommercesThunks';
import { ECOMMERCE_NOTICE } from '../../../../../features/ecommerces/ecommercesFeedback';
import { notify } from '../../../../../features/feedback/notify';
import { MELI_SLUG } from '../../../../../features/ecommerces/ecommercesPlatforms';
import { rememberOauthStart } from '../../../../../features/ecommerces/oauthReturn';

/**
 * Shown instead of the Mercado Libre form while its authorization never came
 * back. The form's own "Vincular" cannot help there: the backend refuses a
 * second start on a warehouse that already has a record, pending or not.
 */
const MeliPendingBanner = ({ integration }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [retrying, setRetrying] = useState(false);

  const handleRetry = async () => {
    setRetrying(true);
    try {
      const { url, integrationId } = await dispatch(retryMeliAuth(integration)).unwrap();
      if (integrationId) rememberOauthStart({ id: integrationId, platform: MELI_SLUG });
      window.location.replace(url);
    } catch (error) {
      setRetrying(false);
      setIsConfirmOpen(false);
      dispatch(notify.error(error?.message || 'No se pudo reintentar la vinculación.', {
        testId: ECOMMERCE_NOTICE,
      }));
      // The pending record is gone, so this page has nothing left to show.
      if (error?.removed) navigate('/configuration/ecommerce', { replace: true });
    }
  };

  return (
    <>
      <div
        data-testid="meli-pending"
        className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-900 rounded-2xl p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
      >
        <div className="flex items-start gap-3">
          <Clock size={20} className="text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-ninja-dark dark:text-white">
              La autorización con Mercado Libre no se completó
            </p>
            <p className="text-sm text-text-secondary mt-1">
              Mientras quede pendiente, esta bodega no puede vincular otra cuenta. Al reintentar se
              elimina este registro y se vuelve a abrir Mercado Libre.
            </p>
          </div>
        </div>
        <Button
          variant="primary"
          icon={RefreshCw}
          onClick={() => setIsConfirmOpen(true)}
          data-testid="meli-retry"
          className="flex-shrink-0"
        >
          Reintentar vinculación
        </Button>
      </div>

      <ConfirmDialog
        isOpen={isConfirmOpen}
        onClose={() => !retrying && setIsConfirmOpen(false)}
        onConfirm={handleRetry}
        title="Reintentar vinculación"
        message="Se elimina el registro pendiente y se vuelve a abrir Mercado Libre para autorizar la cuenta."
        confirmLabel="Reintentar"
        loadingLabel="Redirigiendo..."
        loading={retrying}
        testId="meli-retry-dialog"
      />
    </>
  );
};

export default memo(MeliPendingBanner);
